import React, { useState } from "react";
import { Alert, Button, Select, Tooltip, App } from "antd";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "../../../components/DataTable";
import ActivityIndicator from "../../../components/ActivityIndicator";
import { resolveResourceType } from "../../../components/Forms/LessonForm/utils";
import { ResourceResponse } from "../../../types/api/getResources";
import { ResourceType } from "../../../types";
import { apiSlice } from "../../../store/api/apiSlice";
import {
  useGetResourcesQuery,
  useUpdateResourceTitleMutation,
  useDeleteResourceMutation,
  useLazyGetResourceHomeworkImpactQuery,
} from "../../../store/api/resourcesApi";
import InlineTextEdit from "../../../components/InlineTextEdit";
import TableSearchBar from "../../../components/TableSearchBar";
import { useTableUrlState } from "../../../hooks/useTableUrlState";
import { showDeleteUndoNotification } from "../../../utils/delete-undo";
import PageHeader from "../../../components/PageHeader";
import LinkButton from "../../../components/LinkButton";
import { useIsInitialLoad } from "../../../hooks/useIsInitialLoad";
import ResourceHomeworkImpactModal from "../../../components/ResourceHomeworkImpactModal";
import type {
  HomeworkResolutionDto,
  ResourceHomeworkImpact,
} from "../../../types/api/resourceHomeworkImpact";

// URL param name → commit mode; the URL is the source of truth for the
// filters and the (client-side) pagination page: the list is deep-linkable
// and survives refreshes and back/forward.
const RESOURCES_FILTERS = {
  id: "debounced",
  title: "debounced",
  type: "immediate",
} as const;

const RESOURCE_TYPES = [
  { value: "", label: "Todos los tipos" },
  { value: "VIDEO", label: "Video" },
  { value: "DOWNLOAD", label: "Descarga" },
  { value: "GAME", label: "Juego" },
  { value: "SLIDES", label: "Diapositiva" },
  { value: "BOOK", label: "Libro" },
  { value: "AUDIO", label: "Audio" },
  { value: "READING_KINDER", label: "Lectura Kinder" },
  { value: "READING_PRIMARIA", label: "Lectura Primaria" },
  { value: "DIAGNOSTIC", label: "Diagnóstico" },
];

const isPrimaria = (resource: ResourceResponse): boolean =>
  resource.type === ResourceType.READING && !!resource.reading_mode;

const Resources: React.FC = () => {
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { page, pageSize, filters, inputs, setFilter, onPageChange } =
    useTableUrlState({ filters: RESOURCES_FILTERS });

  // The backend has no search params usable by this page, so the query loads
  // the full list and the filtering below stays client-side. `data` (instead
  // of `currentData`) keeps the previous list visible while refetching.
  const { data, isFetching, isError, refetch } = useGetResourcesQuery();
  const [deleteResource] = useDeleteResourceMutation();
  const [updateResourceTitle] = useUpdateResourceTitleMutation();
  const [fetchHomeworkImpact] = useLazyGetResourceHomeworkImpactQuery();
  const [pendingImpact, setPendingImpact] = useState<{
    record: ResourceResponse;
    impact: ResourceHomeworkImpact;
  } | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const resources = data ?? [];

  const filteredResources = resources.filter((resource) => {
    const matchesTitle = resource.title
      .toLowerCase()
      .includes(filters.title.toLowerCase());
    const matchesId = resource.id.toString().includes(filters.id);

    let matchesType = true;
    if (filters.type === "READING_KINDER") {
      matchesType =
        resource.type === ResourceType.READING && !isPrimaria(resource);
    } else if (filters.type === "READING_PRIMARIA") {
      matchesType = isPrimaria(resource);
    } else if (filters.type !== "") {
      matchesType = resource.type === filters.type;
    }

    return matchesTitle && matchesId && matchesType;
  });

  const isInitialLoad = useIsInitialLoad(isFetching, resources);

  const handleEdit = (record: ResourceResponse) => {
    navigate(`/resources/${record.id}`, { state: record.id });
  };

  const runDelete = async (
    record: ResourceResponse,
    options: { confirm?: boolean; acknowledgeInFlight?: boolean } = {},
  ) => {
    try {
      await deleteResource({ id: record.id, ...options }).unwrap();
      // La notificación de éxito es la propia notificación de "Deshacer"; si
      // la restauración ocurre, invalidar el tag refresca la lista cacheada.
      showDeleteUndoNotification({
        entity: "resource",
        id: record.id,
        title: record.title,
        onRestored: () => {
          dispatch(
            apiSlice.util.invalidateTags([
              { type: "Resource", id: "LIST" },
              "Trash",
            ]),
          );
        },
      });
    } catch (error: any) {
      notification["error"]({
        message: "Eliminación Erronea",
        description:
          error?.data?.message || "El recurso no pudo ser eliminado!",
        placement: "top",
        duration: 3,
      });
    }
  };

  // SCRUM-1020: a delete now pre-fetches the homework impact. Zero-homework
  // deletes stay exactly as before (direct delete + undo toast); an
  // affected resource opens the impact modal instead — only "withdraw" is
  // offered, since a delete has no replacement.
  const handleDelete = async (record: ResourceResponse) => {
    const { data: impact } = await fetchHomeworkImpact(record.id);
    const affected = impact?.[0];
    if (affected && affected.homework_count > 0) {
      setPendingImpact({ record, impact: affected });
      return;
    }
    await runDelete(record);
  };

  const handleImpactConfirm = async (resolutions: HomeworkResolutionDto[]) => {
    if (!pendingImpact) return;
    const { record } = pendingImpact;
    const acknowledgeInFlight = resolutions[0]?.acknowledge_in_flight;
    setPendingImpact(null);
    setConfirmingDelete(true);
    try {
      await runDelete(record, { confirm: true, acknowledgeInFlight });
    } finally {
      setConfirmingDelete(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Recursos"
        extra={
          <TableSearchBar
            fields={[
              {
                placeholder: "Buscar por ID",
                value: inputs.id,
                onChange: (value) => setFilter("id", value),
                width: 120,
              },
              {
                placeholder: "Buscar por título",
                value: inputs.title,
                onChange: (value) => setFilter("title", value),
                width: 200,
              },
            ]}
          >
            <Select
              style={{ width: 200 }}
              value={inputs.type}
              onChange={(value) => setFilter("type", value ?? "")}
              options={RESOURCE_TYPES}
              placeholder="Filtrar por tipo"
              allowClear
            />
            <LinkButton type="primary" to="/resources/new">
              Nuevo recurso
            </LinkButton>
          </TableSearchBar>
        }
      />
      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="No se pudieron cargar los recursos."
          action={
            <Button size="small" onClick={() => refetch()}>
              Reintentar
            </Button>
          }
        />
      )}
      {isInitialLoad ? (
        <ActivityIndicator />
      ) : (
        <DataTable
          data={filteredResources}
          columns={[
            { title: "ID", dataIndex: "id", key: "id" },
            {
              title: "Título",
              dataIndex: "title",
              key: "title",
              // Inline rename: a safe partial PATCH (only the title travels),
              // so a small fix never requires opening the full form.
              render: (title: string, record: ResourceResponse) => (
                <InlineTextEdit
                  value={title}
                  editLabel={`Editar título de ${title}`}
                  successMessage="Título del recurso actualizado."
                  onSave={(next) =>
                    updateResourceTitle({ id: record.id, title: next }).unwrap()
                  }
                />
              ),
            },
            {
              title: "Tipo de Recurso",
              dataIndex: "type",
              key: "type",
              render: (_type: ResourceType, record: ResourceResponse) => {
                if (record.type === ResourceType.READING) {
                  return isPrimaria(record)
                    ? "Lectura Primaria"
                    : "Lectura Kinder";
                }
                return resolveResourceType(record.type);
              },
            },
            {
              title: "Programas",
              dataIndex: "programs",
              key: "programs",
              width: 220,
              // Tras una duplicación varios recursos comparten título; el
              // programa al que pertenece cada copia (vía programa, curso o
              // actividades) es lo que las distingue.
              render: (
                programs: ResourceResponse["programs"],
              ): React.ReactNode => {
                if (!programs || programs.length === 0) {
                  return "NO ASIGNADO";
                }
                const programsText = programs
                  .map((program) => program.program_code ?? program.title)
                  .join(", ");
                return (
                  <Tooltip title={programsText} placement="topLeft">
                    <div
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <strong>{programsText}</strong>
                    </div>
                  </Tooltip>
                );
              },
            },
          ]}
          onEdit={handleEdit}
          getEditHref={(record) => `/resources/${record.id}`}
          onDelete={handleDelete}
          loading={isFetching}
          pagination={{
            current: page,
            pageSize,
            total: filteredResources.length,
            onChange: onPageChange,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
        />
      )}
      <ResourceHomeworkImpactModal
        open={!!pendingImpact}
        impact={pendingImpact ? [pendingImpact.impact] : []}
        confirmLoading={confirmingDelete}
        onCancel={() => setPendingImpact(null)}
        onConfirm={handleImpactConfirm}
      />
    </div>
  );
};

export default Resources;
